import { useCallback, useEffect, useId, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { token } from '@bp-files/ui';
import type { FileSummary } from '@/lib/api';
import { ViewerFrame } from '@/components/Viewer/ViewerFrame';
import { useFileBlob } from '@/components/Viewer/useFileBlob';
import { VIEWER_COPY } from '@/components/Viewer/viewer.copy';
import { isForeignKey } from '@/components/Viewer/keyboard';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { MOBILE_QUERY } from '@/lib/breakpoints';
import { openPdf, PdfOpenError, type PdfDocumentHandle } from './pdfDocument';
import { GUTTER, PdfStage } from './PdfStage';
import {
  clampPdfScale,
  fitScale,
  nextZoomStep,
  normalizeRotation,
  rotatedSize,
  type FitMode,
  type Size,
} from './pdfPageLayout';
import { PDF_VIEWER_COPY } from './pdfViewer.copy';
import { PdfBottomBar } from './PdfBottomBar';
import { PdfMobileMenu } from './PdfMobileMenu';
import { PdfToolbarDesktop } from './PdfToolbarDesktop';
import type { PdfControls } from './pdfControls';
import { DrawLayer } from './Signing/DrawLayer';
import { FieldLayer } from './Signing/FieldLayer';
import { useSigning } from './Signing/useSigning';
import type { FieldRect, PageView } from './Signing/signField';

/**
 * The full-screen PDF reader (FIL-8b).
 *
 * **The controls mirror the browser's own, deliberately.** Page position with a
 * box to type into, zoom out and in either side of a percentage, a fit toggle,
 * and rotate — in that order, because that is the order a reader has already
 * learned somewhere else. Nothing here is novel and nothing should be: the
 * product of this screen is the document.
 *
 * **The chrome is one shape on a desktop and a different shape on a phone, and
 * the branch is a real mount rather than `hidden lg:block`.** Above 1024px
 * everything lives in the frame's one header and there is no footer at all; at
 * or below it the header carries close, the filename and an overflow, and a
 * bottom bar carries paging and the signing tools where a thumb can reach them.
 * `PdfToolbarDesktop` and `PdfBottomBar` each carry the argument for their own
 * layout. Two reasons it is not a CSS toggle: two copies of a control in the
 * DOM are two things answering to "Previous page", which every query in this
 * component's tests would break on; and the phone deliberately renders *fewer*
 * controls, not the same ones smaller.
 *
 * **There is no zoom chrome on a phone: pinch is the zoom.** `PdfStage` owns
 * the whole gesture — it magnifies the drawn pages while the fingers are down
 * and reports one scale when they lift (ADR-0101), so this component never sees
 * an intermediate value and the percentage readout never flickers. Nothing here
 * may ever set `touch-action: none` or `user-scalable=no` to make room for a
 * `−`/`+` pair: `apps/web/index.html` leaves user scaling on because
 * declining pinch on one scroll container is a decision about one document
 * where disabling page zoom is a decision about somebody's eyesight, and
 * `e2e/signing.mobile.spec.ts` asserts as much.
 *
 * **There is no previous and next file.** `ImageViewer` steps because a folder
 * of photographs is a set you compare across; a document is one thing you stay
 * inside, so the pager counts pages instead and the folder is reached by
 * closing. FIL-8b fixes that difference rather than leaving it to taste.
 *
 * **Opening this is an audited download**, the same as opening a photograph —
 * one `file.downloaded` row, one metered transfer, no second read path
 * (UPL-8, ADR-0053). Paging and zooming cost nothing further: the bytes are
 * already in the tab and each page is re-rendered from them.
 */

/** What the reader lands on. Fit-width is how a document is read. */
const DEFAULT_FIT: FitMode = 'width';

export interface PdfViewerProps {
  file: FileSummary;
  onClose: () => void;
  /** Runs the row's ordinary download — the same act, from inside the frame. */
  onDownload: () => void;
  downloadLabel: string;
  downloadBusy: boolean;
  /**
   * FIL-8c. Signing produces an upload, so it needs exactly what an upload
   * needs — and a read-only member never uploads (PERM-4a). Visibility only; the API
   * refuses the upload session regardless (SEC-5).
   */
  canSign: boolean;
  spaceId: string;
  folderId: string;
  /** Called once a signed copy has landed, so the folder can refetch. */
  onSigned: () => void;
}

export function PdfViewer({
  file,
  onClose,
  onDownload,
  downloadLabel,
  downloadBusy,
  canSign,
  spaceId,
  folderId,
  onSigned,
}: PdfViewerProps) {
  const titleId = useId();
  const location = useLocation();
  const navigate = useNavigate();
  const { state, retry } = useFileBlob(file.id, file.sizeBytes);

  const [document, setDocument] = useState<PdfDocumentHandle | undefined>(undefined);
  const [openFailure, setOpenFailure] = useState<PdfOpenError | undefined>(undefined);
  const [firstPage, setFirstPage] = useState<{ size: Size; rotation: number } | undefined>(
    undefined,
  );

  const [stageSize, setStageSize] = useState<Size>({ width: 0, height: 0 });
  const [fitMode, setFitMode] = useState<FitMode | undefined>(DEFAULT_FIT);
  const [chosenScale, setChosenScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [scrollToPage, setScrollToPage] = useState<number | undefined>(undefined);

  const blob = state.kind === 'ready' ? state.blob : undefined;

  /*
    Everything about signing lives in one hook, so this component stays the
    frame it was before FIL-8c: the toolbar, the stage and the footer. The hook
    owns the placed fields, the stored mark, and the save.
  */
  const signing = useSigning({ file, blob, spaceId, folderId, enabled: canSign, onSigned });

  /**
   * Parse the bytes once they have arrived.
   *
   * The document is destroyed on the way out, and that is not tidiness: it
   * terminates the worker. A reader who opens six filings in a session would
   * otherwise leave six workers running, each holding its own copy of a
   * document's operator lists.
   */
  useEffect(() => {
    if (blob === undefined) return;

    let live = true;
    let opened: PdfDocumentHandle | undefined;

    async function open(bytes: Blob): Promise<void> {
      try {
        const handle = await openPdf(await bytes.arrayBuffer());
        if (!live) {
          void handle.destroy();
          return;
        }

        opened = handle;
        const page = await handle.getPage(0);
        if (!live) return;

        setDocument(handle);
        setFirstPage({ size: page.size, rotation: page.rotation });
      } catch (caught) {
        if (!live) return;
        setOpenFailure(
          caught instanceof PdfOpenError ? caught : new PdfOpenError('unknown', caught),
        );
      }
    }

    void open(blob);

    return () => {
      live = false;
      void opened?.destroy();
      setDocument(undefined);
      setFirstPage(undefined);
    };
  }, [blob]);

  /**
   * The scale actually drawn at.
   *
   * Derived rather than stored, so that resizing the window keeps a fitted
   * document fitted without an effect writing state on every resize frame. The
   * moment the reader zooms, `fitMode` clears and their number wins.
   */
  const displayedFirstPage =
    firstPage === undefined
      ? undefined
      : rotatedSize(firstPage.size, normalizeRotation(firstPage.rotation + rotation));

  const scale =
    fitMode !== undefined && displayedFirstPage !== undefined
      ? fitScale(stageSize, displayedFirstPage, fitMode, GUTTER)
      : chosenScale;

  const zoomBy = useCallback(
    (direction: 1 | -1) => {
      setChosenScale(nextZoomStep(scale, direction));
      setFitMode(undefined);
    },
    [scale],
  );

  /**
   * A pinch, which lands on the same two pieces of state the buttons do.
   *
   * Continuous rather than laddered — `nextZoomStep` is the right answer for a
   * press, which is a discrete request for "a bit more", and the wrong one for
   * a gesture that is already telling you exactly how much. Clamped here rather
   * than in the stage so `MIN_SCALE`/`MAX_SCALE` stay `pdfPageLayout`'s to know;
   * the stage clamps its *preview* against the same function so the pages do not
   * magnify past a limit this would then snap back from.
   *
   * **Once per gesture, not once per move** (ADR-0101). This used to run on
   * every touch sample and that is what made a pinch jerky — see `PdfStage`'s
   * `onPinch` prop for the mechanism. It now arrives when a finger lifts.
   */
  const onPinch = useCallback((next: number) => {
    setChosenScale(clampPdfScale(next));
    setFitMode(undefined);
  }, []);

  const onStageResize = useCallback((size: Size) => {
    setStageSize(size);
  }, []);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setScrollToPage(undefined);
  }, []);

  const pageCount = document?.pageCount ?? 0;

  const goToPage = useCallback(
    (asked: number) => {
      // Clamped, never wrapped — `ImageViewer`'s rule, and typing 900 into a
      // six-page filing should land on six rather than on one.
      if (!Number.isFinite(asked)) return;
      const page = Math.min(pageCount, Math.max(1, Math.trunc(asked)));
      setCurrentPage(page);
      setScrollToPage(page);
    },
    [pageCount],
  );

  // The pencil freezes the view. A zoom, fit or rotate mid-drawing would move
  // the page under strokes captured in screen pixels; a pinch would fight the
  // draw gesture outright. `PdfStage` withholds the pinch below, the bars grey
  // the buttons, and the keyboard shortcuts stand down here.
  const drawing = signing.tool === 'draw';

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // Somebody typing owns their own keystrokes, and Ctrl/Cmd belongs to the
      // browser — `Viewer/keyboard.ts` has the argument and both readers share
      // the answer.
      if (isForeignKey(event)) return;

      // The zoom shortcuts are locked while drawing, for the reason above.
      if (drawing) return;

      // Escape belongs to the frame. Everything here is about this document.
      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault();
          zoomBy(1);
          break;
        case '-':
          event.preventDefault();
          zoomBy(-1);
          break;
        case '0':
          event.preventDefault();
          setFitMode(DEFAULT_FIT);
          break;
        default:
          break;
      }
    }

    // **Registered at every width, including the one with no zoom buttons.**
    // `MOBILE_QUERY` is a width query, so a 1023px-wide desktop window matches
    // it while absolutely having a keyboard — unregistering there would break
    // zoom for a real reader with no visible cause. The handler only writes
    // `chosenScale`/`fitMode`, which `PdfStage` still consumes on a phone.
    window.document.addEventListener('keydown', onKeyDown);
    return () => {
      window.document.removeEventListener('keydown', onKeyDown);
    };
  }, [zoomBy, drawing]);

  const isMobile = useMediaQuery(MOBILE_QUERY);
  const documentReady = document !== undefined;

  const controls: PdfControls = {
    titleId,
    pageCount,
    currentPage,
    goToPage,
    scale,
    zoomBy,
    fitMode,
    toggleFit: () => {
      setFitMode(fitMode === 'width' ? 'page' : 'width');
    },
    rotate: () => {
      setRotation((current) => normalizeRotation(current + 90));
    },
    drawing,
    onDownload,
    downloadLabel,
    downloadBusy,
    canSign,
    signing,
    onNeedsSignature: () => {
      // Carries the open document back with it, so the back space on the
      // signature screen can return here instead of stranding the reader on an
      // account page.
      void navigate('/settings/signature', {
        state: { from: `${location.pathname}${location.search}` },
      });
    },
  };

  return (
    <ViewerFrame
      title={file.name}
      titleId={titleId}
      onClose={signing.requestClose(onClose)}
      onEscape={signing.onEscape}
      {...(isMobile
        ? {
            /*
              Conditional spread, not `menuItems={isMobile ? … : undefined}`.
              `exactOptionalPropertyTypes` is on, so passing `undefined` to an
              optional prop is a type error — and it is also what guarantees the
              `⋯` trigger never renders on a desktop, since `ViewerFrame` gates
              it on `menuItems !== undefined`.
            */
            closePlacement: 'start' as const,
            menuItems: <PdfMobileMenu controls={controls} documentReady={documentReady} />,
            ...(documentReady ? { footer: <PdfBottomBar controls={controls} /> } : {}),
          }
        : {
            toolbar: <PdfToolbarDesktop controls={controls} documentReady={documentReady} />,
          })}
    >
      {/*
        **The signing refusal is a strip, not a line inside a bar.**

        It used to sit in the footer, which no longer exists on a desktop — and
        it never really fitted there anyway: `signing.error` is `messageFrom`'s
        output, an arbitrary-length sentence from the API (a 429 naming a cap, a
        403 on a observer space). Squeezed into a control strip it either
        truncates the half that says what to do or blows the header's width
        budget. Full-width between the chrome and the document, it is one
        implementation serving both breakpoints, and it reads where a reader
        looks for it.
      */}
      {signing.error === undefined ? null : (
        <p
          role="alert"
          className="shrink-0 border-b px-4 py-2 text-xs"
          style={{ borderColor: token('bp-border'), color: token('bp-danger') }}
        >
          {signing.error}
        </p>
      )}

      {document !== undefined ? (
        <PdfStage
          document={document}
          scale={clampPdfScale(scale)}
          rotation={rotation}
          onStageResize={onStageResize}
          onPageChange={onPageChange}
          scrollToPage={scrollToPage}
          /*
            Withheld while the pencil is armed, which locks zoom: `PdfStage`'s
            pinch listener only binds when this is defined and re-binds on its
            identity, so an undefined `onPinch` unbinds it for the duration.
          */
          {...(drawing ? {} : { onPinch })}
          {...(canSign
            ? {
                // Rendered for any writer, not only one with a stored mark — the
                // pencil needs none. `FieldLayer` is inert without an armed tool
                // (`pointerEvents: none`), so an always-mounted overlay is safe.
                renderOverlay: (pageIndex: number, pageView: PageView) => (
                  <>
                    <FieldLayer
                      pageIndex={pageIndex}
                      view={pageView}
                      fields={signing.fields}
                      tool={signing.tool}
                      ink={signing.ink}
                      onPlace={signing.place}
                      onChange={(id: string, rect: FieldRect) => {
                        signing.move(id, rect);
                      }}
                      onText={signing.setText}
                      onRemove={signing.remove}
                      onEndEdit={signing.endEdit}
                    />
                    {signing.tool === 'draw' ? (
                      <DrawLayer
                        // Remount on undo so the canvas re-seeds from the
                        // restored buffer — the layer owns its strokes after
                        // mount, so nothing else makes an undone stroke leave it.
                        key={`draw-${String(pageIndex)}-${String(signing.drawGeneration)}`}
                        pageIndex={pageIndex}
                        view={pageView}
                        strokes={signing.drawStrokes[pageIndex]?.strokes ?? []}
                        onStrokesChange={signing.setDrawStrokes}
                      />
                    ) : null}
                  </>
                ),
              }
            : {})}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4">
          {state.kind === 'error' || openFailure !== undefined ? (
            <>
              <p
                className="max-w-md text-center text-sm"
                style={{ color: token('bp-text-secondary') }}
              >
                {openFailure === undefined
                  ? state.kind === 'error'
                    ? state.message
                    : VIEWER_COPY.errors.mint
                  : PDF_VIEWER_COPY.errors[openFailure.reason]}
              </p>
              {/*
                A retry is offered for a transfer that failed, and withheld for a
                document that will not open: a password-protected filing will be
                password-protected on the next attempt too, and a button that
                cannot work is worse than no button.
              */}
              {openFailure === undefined ? (
                <button
                  type="button"
                  onClick={retry}
                  className="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-elevated"
                  style={{ borderColor: token('bp-border') }}
                >
                  {VIEWER_COPY.errors.retry}
                </button>
              ) : null}
            </>
          ) : (
            <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
              {state.kind === 'loading' && state.totalBytes > 0
                ? VIEWER_COPY.loadingProgress(
                    Math.min(100, Math.floor((state.receivedBytes / state.totalBytes) * 100)),
                  )
                : PDF_VIEWER_COPY.loading}
            </p>
          )}
        </div>
      )}
    </ViewerFrame>
  );
}
